/**
 * Ana sayfa kartlarını hücre ızgarasına yerleştirir (kural ve hesap
 * `lib/hucreIzgarasi.ts`). Kartın bir genişlikte kaç satır gerektirdiği,
 * kartın görünmez bir kopyası o genişlikte çizilerek ölçülür.
 *
 * Yerleşim; kap genişliği değişince ve kartların içeriği değişince (özetin
 * yüklenmesi, "?" ile tanıtıma dönüş) yeniden hesaplanır. Yerleri React
 * değil bu bileşen yazar (stil özniteliği): her ölçüm yeniden çizim
 * döngüsü başlatmasın diye. Dar ekranda (iki kart yan yana sığmıyorsa) ve
 * ölçüm yapılamayan ortamda tek sütun.
 */

import { Children, useLayoutEffect, useRef, type ReactNode } from "react";
import { ARALIK, HUCRE, TABAN_PX, hucrePiksel, hucreSayisi, sutunSayisi, yerlesim } from "../lib/hucreIzgarasi";

const TEK_SUTUN_ARALIK = "16px";

export function HucreIzgarasi({ children }: { readonly children: ReactNode }) {
  const kap = useRef<HTMLDivElement>(null);
  const izgara = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const k = kap.current;
    const z = izgara.current;
    if (k === null || z === null) return undefined;

    const tekSutun = (): void => {
      Object.assign(z.style, { display: "flex", flexDirection: "column", gap: TEK_SUTUN_ARALIK, gridTemplateColumns: "", gridAutoRows: "" });
      for (const el of Array.from(z.children) as HTMLElement[]) {
        el.style.gridColumn = "";
        el.style.gridRow = "";
      }
    };

    const yerlestir = (): void => {
      const stil = getComputedStyle(k);
      const genislik = k.clientWidth - parseFloat(stil.paddingLeft || "0") - parseFloat(stil.paddingRight || "0");
      const sutun = sutunSayisi(genislik);
      const kartlar = (Array.from(z.children) as HTMLElement[]).filter((el) => el.firstElementChild !== null);
      const olcu = document.createElement("div");
      Object.assign(olcu.style, { position: "absolute", left: "0", top: "0", visibility: "hidden", pointerEvents: "none" });
      olcu.setAttribute("aria-hidden", "true");
      k.appendChild(olcu);
      try {
        const yerler = yerlesim(
          kartlar.map((el) => ({
            taban: hucreSayisi(TABAN_PX),
            satirIhtiyaci: (g: number) => {
              const kopya = el.firstElementChild!.cloneNode(true) as HTMLElement;
              olcu.style.width = `${hucrePiksel(g)}px`;
              olcu.replaceChildren(kopya);
              const h = kopya.getBoundingClientRect().height;
              return h > 0 ? hucreSayisi(h) : Number.NaN;
            },
          })),
          sutun,
        );
        // Ölçülemeyen ortam (yükseklik 0) ya da dar ekran: tek sütun.
        if (yerler === null || yerler.some((y) => !Number.isFinite(y.yukseklik))) {
          tekSutun();
          return;
        }
        Object.assign(z.style, {
          display: "grid",
          flexDirection: "",
          gap: `${ARALIK}px`,
          gridTemplateColumns: `repeat(${sutun}, ${HUCRE}px)`,
          gridAutoRows: `${HUCRE}px`,
        });
        kartlar.forEach((el, i) => {
          const y = yerler[i]!;
          el.style.gridColumn = `${y.sutun + 1} / span ${y.genislik}`;
          el.style.gridRow = `${y.satir + 1} / span ${y.yukseklik}`;
        });
      } finally {
        olcu.remove();
      }
    };

    let bekleyen = 0;
    const planla = (): void => {
      cancelAnimationFrame(bekleyen);
      bekleyen = requestAnimationFrame(yerlestir);
    };
    yerlestir();

    let sonGenislik = k.clientWidth;
    const boyut = new ResizeObserver(() => {
      if (k.clientWidth !== sonGenislik) {
        sonGenislik = k.clientWidth;
        planla();
      }
    });
    boyut.observe(k);
    // Yalnızca içerik değişimi: kendi yazdığımız stil öznitelikleri izlenmez.
    const icerik = new MutationObserver(planla);
    icerik.observe(z, { childList: true, subtree: true, characterData: true });
    return () => {
      cancelAnimationFrame(bekleyen);
      boyut.disconnect();
      icerik.disconnect();
    };
  }, []);

  return (
    <div ref={kap} className="relative w-full p-4">
      <div ref={izgara} data-hucre-izgarasi="" className="flex flex-col gap-4">
        {Children.map(children, (kart) => (
          <div className="min-w-0 empty:hidden [&>*]:h-full">{kart}</div>
        ))}
      </div>
    </div>
  );
}
